import { useQuery } from '@tanstack/react-query';
import {
  configFetch,
  getTokenForAudience,
  isFullyQualifiedLeagueApiUrl,
} from '@web-config-app/api';
import type { HTTPMethod } from '@web-config-app/core';
import { useConfigProperty } from '@leagueplatform/config';

export interface UseUrlLoaderProps {
  apiUrl?: string;
  path?: string;
  method?: HTTPMethod;
  audience?: string;
}

const localDevelopmentHostnames = new Set([
  'app.local.league.dev',
  'localhost',
  '127.0.0.1',
]);
const localMockApiHostnames = new Set(['localhost', '127.0.0.1']);
const localMockApiPort = '3001';
const localMockApiPathPrefix = '/api/content-metadata/';

const isLocalDevelopmentHost = () =>
  typeof window !== 'undefined' &&
  localDevelopmentHostnames.has(window.location.hostname);

const isSameOrigin = (url: string, base: string) => {
  try {
    return new URL(url, base).origin === new URL(base).origin;
  } catch {
    return false;
  }
};

const isLocalMockApiUrl = (path: string) => {
  try {
    const url = new URL(path);
    return (
      isLocalDevelopmentHost() &&
      url.protocol === 'http:' &&
      localMockApiHostnames.has(url.hostname) &&
      url.port === localMockApiPort &&
      url.pathname.startsWith(localMockApiPathPrefix)
    );
  } catch {
    return false;
  }
};

export const getUrlFromPath = (
  path: string | undefined,
  apiUrl: string | undefined,
) => {
  if (!path) return null;
  if (!apiUrl) return path;
  return isFullyQualifiedLeagueApiUrl(path) || isLocalMockApiUrl(path)
    ? path
    : `${apiUrl}${path.replace(/^\/?/, '/')}`;
};

/**
 * Hook to fetch data from a URL using React Query.
 *
 * constructs a URL based on the provided `path` and `apiUrl`,
 * and fetches data using the `configFetch` function. It leverages React Query
 * to manage the fetching process and caching.
 *
 * Expects the API to conform to JSON:API standards and return a top-level
 * `data` key in the response which contains the relevant data.
 *
 * @param {Object} props - The properties for the hook.
 * @param {string} [props.apiUrl] - The base API URL to prepend to the `path` if the `path` is not fully qualified.
 * @param {string} [props.path] - The path or fully qualified URL to fetch data from.
 * @param {HTTPMethod} [props.method='GET'] - The HTTP method to use for the request (e.g., 'GET', 'POST').
 * @param {string} [props.audience] - Token audience of the environment that serves `apiUrl`. When omitted, the request uses the default token.
 *
 * @returns {Object} - The result of the React Query `useQuery` hook, including data, error, and status.
 */

export const useFetchUrl = ({
  apiUrl,
  path,
  method = 'GET',
  audience,
}: UseUrlLoaderProps) => {
  const { clientId } = useConfigProperty('core');
  const url = path ? getUrlFromPath(path, apiUrl) : undefined;

  const queryFn = async () => {
    // an absolute URL on another API keeps the default token
    const token =
      audience && apiUrl && url && isSameOrigin(url, apiUrl)
        ? await getTokenForAudience(audience)
        : undefined;
    const response = await configFetch(url ?? '', {
      method,
      clientId,
      ...(token && { token }),
    });
    const json = await response.json();
    return json?.data;
  };

  return useQuery({
    enabled: Boolean(url),
    queryKey: [audience, url],
    queryFn,
    retry: 1,
  });
};
